import React, { useMemo, memo, useState } from "react";
import { compose } from "redux";
import { useI18n, useFetchQuery } from "Hooks";
import { connect } from "react-redux";
import _ from "lodash";
import withDerivedProps from "lib/withDerivedProps";

// design system
import {
  EmptyState,
  IconButton,
  Table,
  SearchInput,
  ChecklistDropdown,
  FilterLabelButton,
} from "@toddle-edu/ds-web";
import { ChevronRightOutlined } from "@toddle-edu/ds-icons";
import {
  NoServiceAsActionInsightsIllustration,
  NoSearchInFilterIllustration,
} from "@toddle-edu/ds-theme";

// modules
import {
  getStandardSetsQuery,
  getGradeScalesQuery,
} from "../../modules/StandardsSelectionModalQueries";
import { DATA_SCOPE_ENUM, SEARCH_LABELS } from "../../modules/Constants";
import {
  getFeedListColumns,
  getFilteredStandardSets,
  getStandardSetsQueryVariables,
  hasNodesWithGradeScales,
} from "../../modules/Utils";

// components
import SkeletonLoader from "UIComponents/SkeletonLoader";

// utils
import {
  getIsLearningCoursesEnabled,
  getIsSubjectBasedGradingEnabled,
} from "Teacher/routes/LearningCourses/modules/Utils";
import { localSearch } from "Utils/localSearch";
import { getLabelValueForMultiFilterLabelButton } from "Utils";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";

// constants
import { REGULAR } from "Constants/stringConstants";

// scss
import classes from "./StandardsSetTable.scss";

const styles = {
  whiteBG: {
    backgroundColor: "var(--surface-primary-enabled)",
  },
  emptyStateSubtitle: {
    maxWidth: "280px",
  },
};

const EMPTY_ARRAY = [];

const StandardsSetTable = memo(props => {
  const {
    onSelectStandardSet,
    selectedStandardSetDataScope,
    learningCourseId,
    assessmentSubjects,
    assessmentId,
    assessmentTools,
    curriculumProgramId,
    isSubjectBasedGrading,
    isGradeScaleToolPresent,
    academicYearId,
    curriculumProgramType,
    canMapGradeScale = true,
    unitTaggedStandardSetIds,
  } = props;

  const { t } = useI18n();

  // Local state for filters
  const [searchText, setSearchText] = useState("");
  const [selectedGradeScales, setSelectedGradeScales] = useState([]);

  // Fetch grade scale options
  const { data: gradeScaleData } = useFetchQuery({
    query: getGradeScalesQuery,
    variables: (() => {
      const type = "CURRICULUM_PROGRAM";
      return {
        id: curriculumProgramId,
        type,
        filters: {
          isArchived: false,
          entityType: type,
          types: [REGULAR],
          entityDetailFilters: { curriculumProgramId },
        },
      };
    })(),
    skipCondition:
      !curriculumProgramId || isGradeScaleToolPresent || !canMapGradeScale,
    parseData: ({ node } = {}) => {
      const gradeScaleEdges = _.get(node, "gradeScale.edges", []);

      const gradeScaleOptions = _.map(gradeScaleEdges, ({ node }) => ({
        value: _.get(node, "id"),
        label: _.get(node, "displayName"),
      }));

      return { gradeScaleOptions };
    },
  });

  const gradeScaleOptions = gradeScaleData?.gradeScaleOptions || EMPTY_ARRAY;

  // Fetch standard sets
  const {
    data: standardSetsData,
    loading: isStandardSetsLoading,
  } = useFetchQuery({
    query: getStandardSetsQuery,
    variables: getStandardSetsQueryVariables({
      curriculumProgramId,
      curriculumProgramType,
      learningCourseId,
      selectedStandardSetDataScope,
      assessmentSubjects,
      assessmentId,
      academicYearId,
      searchText,
      gradeScaleIds: selectedGradeScales,
    }),
    skipCondition: (() => {
      const hasNoId =
        selectedStandardSetDataScope === DATA_SCOPE_ENUM.LEARNING_COURSE
          ? !learningCourseId
          : !curriculumProgramId;

      const hasNoSubjectsForSubjectBasedGrading =
        isSubjectBasedGrading && assessmentId && _.isEmpty(assessmentSubjects);

      return hasNoId || hasNoSubjectsForSubjectBasedGrading;
    })(),
    fetchPolicy: "cache-and-network",
  });

  const node = _.get(standardSetsData, "node");
  let standardSetsEdges = _.get(node, "plannerElementNodeSets.edges", []);

  if (selectedStandardSetDataScope === DATA_SCOPE_ENUM.LEARNING_COURSE) {
    standardSetsEdges =
      _.get(node, "standardsWrapper.standardsEdge") ||
      _.get(node, "benchmarksWrapper.standardsEdge", []);
  }

  const hasStandardSets = !_.isEmpty(standardSetsEdges);
  const standardSets = getFilteredStandardSets({
    edges: standardSetsEdges,
    assessmentTools,
    canMapGradeScale,
  });
  const standardSetsHasNodesWithGradeScales = hasNodesWithGradeScales({
    edges: standardSetsEdges,
    assessmentTools,
    canMapGradeScale,
  });
  const isData = !_.isEmpty(node);
  const isLoading = isStandardSetsLoading;

  const headers = useMemo(() => getFeedListColumns({ canMapGradeScale, t }), [
    t,
    canMapGradeScale,
  ]);

  const onRowClick = id => {
    if (!canMapGradeScale) {
      const standardSet = _.find(standardSets, { id });
      onSelectStandardSet({ standardSet, gradeScale: null });
      return;
    }

    const [standardSetId, gradeScaleId] = _.split(id, "-");

    const standardSet = _.find(standardSets, { id: standardSetId });
    const gradeScale = _.find(standardSet?.gradeScales, {
      id: gradeScaleId,
    });

    onSelectStandardSet({ standardSet, gradeScale });
  };

  const searchLabel = SEARCH_LABELS["STANDARD_SET_SELECTION"];

  // Create skeleton loader data when loading
  const skeletonTableData = useMemo(() => {
    if (!canMapGradeScale) {
      return _.times(6, index => ({
        id: `skeleton-${index}`,
        rowData: _.map(headers, ({ key }) => {
          switch (key) {
            case "NAME":
              return {
                key,
                value: <SkeletonLoader height="20px" width="200px" />,
              };
            case "ACTIONS":
              return {
                key,
                value: (
                  <SkeletonLoader
                    height="28px"
                    width="28px"
                    variant="circular"
                  />
                ),
                style: styles.whiteBG,
              };
            default:
              return {
                key,
                value: <SkeletonLoader height="20px" width="100px" />,
              };
          }
        }),
      }));
    }

    // Create a random distribution of grade scales per standard set
    // Total of 8 skeleton rows, distributed among 3-4 standard sets
    const standardSetCount = Math.floor(Math.random() * 2) + 3; // 3-4 standard sets
    const standardSets = [];
    let remainingRows = 8;

    // Distribute the grade scales per standard set
    for (let i = 0; i < standardSetCount && remainingRows > 0; i++) {
      // For the last standard set, use all remaining rows but cap at 3
      if (i === standardSetCount - 1) {
        standardSets.push(Math.min(remainingRows, 3));
        remainingRows -= Math.min(remainingRows, 3);
        // If we still have rows left, distribute them to previous sets or add new ones
        if (remainingRows > 0) {
          standardSets.push(remainingRows);
        }
        break;
      }

      // Otherwise randomly assign 1-3 grade scales to this standard set
      const gradeScaleCount = Math.min(
        Math.floor(Math.random() * 3) + 1,
        3,
        remainingRows
      );
      standardSets.push(gradeScaleCount);
      remainingRows -= gradeScaleCount;
    }

    // Create the skeleton rows based on this distribution
    const rows = [];
    let rowIndex = 0;

    _.forEach(standardSets, gradeScaleCount => {
      for (let i = 0; i < gradeScaleCount; i++) {
        const rowData = _.map(headers, ({ key }) => {
          switch (key) {
            case "NAME":
              // Only add rowSpan to the first row in each standard set
              if (i === 0) {
                return {
                  key,
                  value: <SkeletonLoader height="20px" width="200px" />,
                  rowSpan: gradeScaleCount, // Set rowSpan to the number of grade scales
                };
              }
              // Skip this cell for subsequent rows in the same standard set
              return null;
            case "GRADE_SCALES":
              return {
                key,
                value: <SkeletonLoader height="20px" width="150px" />,
                style: styles.whiteBG,
              };
            case "ACTIONS":
              return {
                key,
                value: (
                  <SkeletonLoader
                    height="28px"
                    width="28px"
                    variant="circular"
                  />
                ),
                style: styles.whiteBG,
              };
            default:
              return {
                key,
                value: <SkeletonLoader height="20px" width="100px" />,
              };
          }
        }).filter(Boolean); // Filter out null values

        rows.push({
          id: `skeleton-${rowIndex}`,
          rowData,
        });

        rowIndex++;
      }
    });

    return rows;
  }, [headers, canMapGradeScale]);

  // Apply client-side filtering for UNIT and LEARNING_COURSE scopes
  const filteredStandardSets = useMemo(() => {
    let filtered = standardSets;

    // Filter by unit-tagged standard set IDs when UNIT scope is active
    if (selectedStandardSetDataScope === DATA_SCOPE_ENUM.UNIT) {
      filtered = _.filter(filtered, standardSet =>
        _.includes(unitTaggedStandardSetIds, standardSet?.id)
      );
    }

    // Apply local search for LEARNING_COURSE and UNIT scopes
    if (
      (selectedStandardSetDataScope === DATA_SCOPE_ENUM.LEARNING_COURSE ||
        selectedStandardSetDataScope === DATA_SCOPE_ENUM.UNIT) &&
      searchText
    ) {
      filtered = _.filter(filtered, standardSet => {
        const { title } = standardSet || {};
        return localSearch({ text: title, searchText }) !== -1;
      });
    }

    return filtered;
  }, [
    selectedStandardSetDataScope,
    searchText,
    standardSets,
    unitTaggedStandardSetIds,
  ]);

  const tableData = useMemo(() => {
    return _.reduce(
      filteredStandardSets,
      (acc, standardSet) => {
        const {
          id: standardSetId,
          title: standardSetLabel,
          gradeScales: _gradeScales,
        } = standardSet || {};

        if (!canMapGradeScale) {
          const rowData = _.reduce(
            headers,
            (cells, { key }) => {
              switch (key) {
                case "NAME":
                  cells.push({
                    key,
                    value: standardSetLabel,
                  });
                  break;
                case "ACTIONS":
                  cells.push({
                    key,
                    value: (
                      <IconButton
                        icon={<ChevronRightOutlined />}
                        variant="neutral"
                        type={"plain"}
                        size={"small"}
                        dsVersion="2.0"
                        onClick={() => {
                          onSelectStandardSet({
                            standardSet,
                            gradeScale: null,
                          });
                        }}
                      />
                    ),
                  });
                  break;
              }
              return cells;
            },
            []
          );

          acc.push({ id: standardSetId, rowData });
          return acc;
        }

        const gradeScales = _.isEmpty(_gradeScales) ? [{}] : _gradeScales;

        const rows = _.reduce(
          gradeScales,
          (rows, gradeScale, index) => {
            const { displayName } = gradeScale;

            const rowData = _.reduce(
              headers,
              (cells, { key }) => {
                switch (key) {
                  case "NAME":
                    if (index === 0) {
                      cells.push({
                        key,
                        value: standardSetLabel,
                        rowSpan: _.size(gradeScales),
                      });
                    }
                    break;
                  case "GRADE_SCALES":
                    cells.push({
                      key,
                      value: displayName,
                      style: styles.whiteBG,
                    });
                    break;
                  case "ACTIONS":
                    cells.push({
                      key,
                      value: (
                        <IconButton
                          icon={<ChevronRightOutlined />}
                          variant="neutral"
                          type={"plain"}
                          size={"small"}
                          dsVersion="2.0"
                          onClick={() => {
                            onSelectStandardSet({ standardSet, gradeScale });
                          }}
                        />
                      ),
                      style: styles.whiteBG,
                    });
                    break;
                }
                return cells;
              },
              []
            );

            const id = gradeScale.id
              ? `${standardSetId}-${gradeScale.id}`
              : `${standardSetId}-${index}`;

            rows.push({ id, rowData });

            return rows;
          },
          []
        );

        return _.uniqBy(_.concat(acc, rows), "id");
      },
      []
    );
  }, [filteredStandardSets, headers, onSelectStandardSet, canMapGradeScale]);

  const getEmptyStateIllustration = () => {
    const hasFilters =
      !_.isEmpty(searchText) || !_.isEmpty(selectedGradeScales);

    if (hasFilters) {
      return NoSearchInFilterIllustration;
    }

    return NoServiceAsActionInsightsIllustration;
  };

  const getEmptyStateTitle = () => {
    const hasFilters =
      !_.isEmpty(searchText) || !_.isEmpty(selectedGradeScales);

    if (hasFilters) {
      return t("common:noResultHeading");
    }

    if (!hasStandardSets) {
      return t("classRoom:no_standard_sets");
    }

    if (canMapGradeScale && !standardSetsHasNodesWithGradeScales) {
      return t("classRoom:no_grade_scales_mapped");
    }

    return "";
  };

  const getEmptyStateSubtitle = () => {
    const hasFilters =
      !_.isEmpty(searchText) || !_.isEmpty(selectedGradeScales);

    if (hasFilters) {
      return t("common:no_items_matching_your_search");
    }

    if (!hasStandardSets) {
      return t("classRoom:no_standard_sets_created_msg");
    }

    if (canMapGradeScale && !standardSetsHasNodesWithGradeScales) {
      return t("classRoom:standard_set_and_grade_scale_not_mapped_msg");
    }

    return "";
  };

  const renderHeader = () => {
    return (
      <header className={classes.headerContainer}>
        <div className={classes.headerLeftContainer}>
          <SearchInput
            value={searchText}
            placeholder={t("common:search_for_a_with_label", {
              label: t(searchLabel),
            })}
            size={"medium"}
            onChange={setSearchText}
            focusSearchBySlash={true}
          />
        </div>
        {canMapGradeScale && !_.isEmpty(gradeScaleOptions) && (
          <div className={classes.headerRightContainer}>
            <ChecklistDropdown
              options={gradeScaleOptions}
              value={selectedGradeScales}
              onChange={setSelectedGradeScales}
              showAllOption={true}
              showSelectedOptionsOnTop={false}
            >
              <FilterLabelButton
                variant={"neutral"}
                type={"outlined"}
                size="medium"
                label={t("academicSetup:grade_scales")}
                labelValue={getLabelValueForMultiFilterLabelButton({
                  options: gradeScaleOptions,
                  selectedOptions: selectedGradeScales,
                  allText: t("common:all"),
                  noneText: t("common:none"),
                })}
              />
            </ChecklistDropdown>
          </div>
        )}
      </header>
    );
  };

  const renderSkeletonLoader = () => {
    return (
      <div className={classes.tableContainer}>
        <Table
          key="skeleton"
          data={skeletonTableData}
          headers={headers}
          showRowBackgroundOnHover={true}
          isHeaderFixed={true}
          showLastRowBorder={true}
          testId="appComponents-standardsSelectionModal-standardsSetTable"
        />
      </div>
    );
  };

  const renderEmptyState = () => {
    return (
      <EmptyState
        illustration={getEmptyStateIllustration()}
        title={getEmptyStateTitle()}
        subtitle={getEmptyStateSubtitle()}
        subtitleStyle={styles.emptyStateSubtitle}
      />
    );
  };

  const renderTable = () => {
    return (
      <div className={classes.tableContainer}>
        <Table
          key={"table-with-data"}
          data={tableData}
          headers={headers}
          showRowBackgroundOnHover={true}
          isHeaderFixed={true}
          showLastRowBorder={true}
          testId="appComponents-standardsSelectionModal-standardsSetTable"
          onRowClick={onRowClick}
        />
      </div>
    );
  };

  const renderContent = () => {
    if (isLoading && !isData) {
      return renderSkeletonLoader();
    }

    if (_.isEmpty(filteredStandardSets)) {
      return renderEmptyState();
    }

    return renderTable();
  };

  // Render header and content
  return (
    <>
      {renderHeader()}
      {renderContent()}
    </>
  );
});

StandardsSetTable.displayName = "StandardsSetTable";

const mapStateToProps = state => {
  const { id: curriculumProgramId, type: curriculumProgramType } =
    _.get(state, "platform.currentCurriculumProgram", {}) || {};

  const academicYearId = getSelectedAcademicYearId(state);

  return {
    curriculumProgramId,
    curriculumProgramType,
    academicYearId,
  };
};

const getDerivedProps = () => {
  const isSubjectBasedGrading =
    !getIsLearningCoursesEnabled() || getIsSubjectBasedGradingEnabled();

  return { isSubjectBasedGrading };
};

export default compose(
  connect(mapStateToProps),
  withDerivedProps(getDerivedProps)
)(StandardsSetTable);
